import { Typography } from '@material-ui/core';
import { Column, Columns, Stack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { FieldArray, Form, FormikProvider, useFormikContext } from 'formik';
import { useState } from 'react';
import { useAppFormik } from 'shared/form/AppFormik';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { Prompt } from 'shared/routing/NavigationBlock';
import {
  CarrierEDIIntegrationDTO,
  VehiclesSetupConnectionDetailsDTO,
  VehiclesSetupPartnerDTO,
  vehiclesSetupConnectionDetailsSchema,
  vehiclesSetupPartnerSchema,
} from '../data/VehiclesDTO';
import { useCarrierEDIIntegrationOptions } from '../data/VehiclesAPI';
import { VehiclesSetupPartnerCard } from './VehiclesSetupPartnerCard';

interface VehiclesSetupConnectionDetailsStepProps {
  savedValues: undefined | VehiclesSetupConnectionDetailsDTO;
  onBack: (values: VehiclesSetupConnectionDetailsDTO) => void;
  onComplete: (
    values: VehiclesSetupConnectionDetailsDTO,
  ) => Promise<APIResponse<CarrierEDIIntegrationDTO>>;
}

function createPartner(): VehiclesSetupPartnerDTO {
  return toFormikValidationSchema(vehiclesSetupPartnerSchema).cast({
    _key: crypto.randomUUID(),
  });
}

function PartnerCardList() {
  const { values } = useFormikContext<VehiclesSetupConnectionDetailsDTO>();
  const { data: options } = useCarrierEDIIntegrationOptions();
  const partners = values.partners ?? [];

  return (
    <FieldArray
      name="partners"
      render={({ push, remove }) => (
        <Stack space="small">
          {partners.map((partner, index) => (
            <VehiclesSetupPartnerCard
              key={partner._key}
              name={`partners.${index}`}
              options={options}
              usedOems={partners
                .filter((_, i) => i !== index)
                .map((p) => p.oem)
                .filter(Boolean)}
              onRemove={
                partners.length > 1
                  ? () => {
                      remove(index);
                    }
                  : undefined
              }
            />
          ))}

          <Button
            variant="text"
            onClick={() => {
              push(createPartner());
            }}
          >
            + Add Another Partner
          </Button>
        </Stack>
      )}
    />
  );
}

export function VehiclesSetupConnectionDetailsStep({
  savedValues,
  onBack,
  onComplete,
}: VehiclesSetupConnectionDetailsStepProps) {
  const [defaultValues] = useState<VehiclesSetupConnectionDetailsDTO>(() => ({
    partners: [createPartner()],
  }));
  const formik = useAppFormik<
    VehiclesSetupConnectionDetailsDTO,
    CarrierEDIIntegrationDTO
  >({
    initialValues: savedValues ?? defaultValues,
    validationSchema: vehiclesSetupConnectionDetailsSchema,
    onSubmit(values) {
      return onComplete(values);
    },
  });

  return (
    <FormikProvider value={formik}>
      <Prompt
        when={
          (formik.dirty || !!savedValues) && formik.status.type !== 'submitted'
        }
        message="Changes have not been saved. Leaving will result in unsaved changes being lost."
      />
      <Form aria-label="Connection details form">
        <Stack space="large">
          <PartnerCardList />

          {typeof formik.errors.partners === 'string' ? (
            <Typography color="error">{formik.errors.partners}</Typography>
          ) : null}

          <Columns space="small">
            <Column width="content">
              <Button
                type="button"
                variant="neutral"
                onClick={() => {
                  onBack(formik.values);
                }}
              >
                Back to Step 1
              </Button>
            </Column>
            <Column width="content">
              <Button
                type="submit"
                variant="primary"
                pending={formik.isSubmitting}
              >
                Complete Setup
              </Button>
            </Column>
          </Columns>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
